import { extendZodWithOpenApi } from '@anatine/zod-openapi';
import { z } from 'zod';
import { AccountIdSchema, AccountNameSchema, AccountRoleSchema } from './models.accounts';
import { DateTimeTzSchema, GenericUserID, GenericUUID } from './models.generic';
import { UserFullnameSchema } from './models.users';

extendZodWithOpenApi(z);

export const AccountMemberSchema = z.object({
  name: UserFullnameSchema,
  avatar_url: z.string().url().nullable(),
  account_id: AccountIdSchema,
  user_id: GenericUserID,
  is_primary_owner: z.boolean().describe('Is this user the primary owner of the account'),
  account_role: AccountRoleSchema,
});

export type AccountMember = z.infer<typeof AccountMemberSchema>;

export const InvitationTypeSchema = z.enum(['one-time', '24-hour']);
export type InvitationType = z.infer<typeof InvitationTypeSchema>;

export const AccountMemberInviteSchema = z.object({
  account_id: AccountIdSchema,
  account_role: AccountRoleSchema,
  account_team_name: AccountNameSchema,
  created_at: DateTimeTzSchema,
  id: GenericUUID,
  /** Ref `invitation_type DB enum */
  invitation_type: InvitationTypeSchema,
  invited_by_user_id: GenericUserID,
  token: z.string().openapi({ example: 'VGVzdGluZyE=' }),
  updated_at: DateTimeTzSchema,
});

export type AccountMemberInvite = z.infer<typeof AccountMemberInviteSchema>;

export const CreateAccountMemberInviteSchema = AccountMemberInviteSchema.pick({
  account_role: true,
  invitation_type: true,
});
export type CreateAccountMemberInvite = z.infer<typeof CreateAccountMemberInviteSchema>;

/**
 * Describes the current role, permission, context, etc of a user in an account
 */
export type AccountMemberRole = z.infer<typeof AccountMemberRoleSchema>;
export const AccountMemberRoleSchema = z.object({
  account_role: AccountRoleSchema,
  is_primary_owner: z.boolean().describe('Is this user the primary owner of the account'),
  is_personal_account: z.boolean().describe('Is this account a personal account'),
  role_has_elevated_permissions: z
    .boolean()
    .describe('Does this role have elevated permissions, aka can do most "admin things"'),
});

export const AcceptAccountMemberInvitationSchema = z.object({
  account_id: AccountIdSchema,
});

export const AccountMemberInvitationByTokenSchema = z.object({
  team_name: AccountNameSchema,
  active: z.boolean().describe('Is this account active'),
});

export type AccountMemberInvitationByToken = z.infer<typeof AccountMemberInvitationByTokenSchema>;

export const AccountMemberRoleChangeSchema = z.object({
  user_id: GenericUserID,
  new_account_role: AccountRoleSchema,
  make_primary_owner: z
    .boolean()
    .describe('Should this user be made the primary owner of the account'),
});

export type AccountMemberRoleChange = z.infer<typeof AccountMemberRoleChangeSchema>;
